import React, { useEffect, useState } from "react";
// import CustomButton from "./CustomButton";
import FollowUpEmail from "./FollowUpEmail";
import { ChevronDown, ChevronUp, MapPin, Users } from "lucide-react";
import CoinStack from "@/assets/svg/CoinStack";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { getCleanName, getDisplayLocation } from "@/shared/utils/accountConfig";
import {
  formatPeopleCountShort,
  formatRevenueShort,
} from "@/shared/utils/helperFunctions";
import InlineCitationText from "./AccountProfile/InlineCitationText";
import { sanitizeCompanyLogoUrl } from "@/features/dashboard/utils/accountsDashboardUtils";

const ProfileHeader = ({
  profile,
  revenue,
  contentRef,
  summaryOpen,
  setSummaryOpen,
  headerAction = null,
}) => {
  const [showFollowUpEmail, setShowFollowUpEmail] = useState(false);

  const revenueLabel = formatRevenueShort(revenue?.value ?? profile?.revenue);
  const hasHeadcountResearch = profile?.headcount != null;
  const hasKnownHeadcountRegion =
    !hasHeadcountResearch ||
    getDisplayLocation(profile?.headcount?.region) !== "Not Identified";

  useEffect(() => {
    if (contentRef.current) {
      if (summaryOpen) {
        requestAnimationFrame(() => {
          if (contentRef.current) {
            const height = contentRef.current.scrollHeight;
            contentRef.current.style.maxHeight = height + "px";
            contentRef.current.style.marginTop = "4px";
            contentRef.current.style.opacity = "1";
          }
        });
      } else {
        contentRef.current.style.maxHeight = "0px";
        contentRef.current.style.marginTop = "0px";
        contentRef.current.style.opacity = "0";
      }
    }
  }, [summaryOpen, profile?.profile_summary, contentRef]);

  return (
    <div className="flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded-lg w-full">
      <div className="flex w-full items-center justify-between gap-3">
        <div className="flex min-w-0 gap-x-2.5 items-center">
          <CommonAvatar
            imageUrl={sanitizeCompanyLogoUrl(profile?.company_logo)}
            name={getCleanName(profile?.company_name)}
            alt="persona"
            className="h-14 w-14 rounded-[4px]"
            textClassName="text-lg font-semibold"
          />
          <div className="flex flex-col gap-1.5 items-start font text-gray-800 dark:text-gray-200 font-normal">
            <h1 className="flex gap-2 items-center font-semibold leading-5 text-sm text-gray-900 dark:text-gray-100">
              {getCleanName(profile?.company_name)}{" "}
              {!summaryOpen ? (
                <ChevronDown
                  className="w-5 h-5 text-gray-800 dark:text-gray-200 cursor-pointer"
                  onClick={() => setSummaryOpen(true)}
                />
              ) : (
                <ChevronUp
                  className="w-5 h-5 text-gray-800 dark:text-gray-200 cursor-pointer"
                  onClick={() => setSummaryOpen(false)}
                />
              )}
            </h1>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-[0.763rem] font-medium text-gray-700 dark:text-gray-300">
                <Users size={15} className="text-gray-800 dark:text-gray-300" strokeWidth={2.2} />
                {formatPeopleCountShort(
                  hasKnownHeadcountRegion
                    ? profile?.headcount?.value || profile?.company_size
                    : null,
                )}
              </span>
              <span className="flex items-center gap-1.5 text-[0.763rem] font-medium text-gray-700 dark:text-gray-300">
                {" "}
                <CoinStack />
                {revenueLabel}
              </span>
              <span className="flex items-center gap-1.5 text-[0.763rem] font-medium text-gray-700 dark:text-gray-300">
                <MapPin size={15} className="text-gray-800 dark:text-gray-300" strokeWidth={2.2} />
                {getDisplayLocation(profile?.location)}
              </span>
            </div>
          </div>
        </div>

        {headerAction ? (
          <div className="ml-auto shrink-0">{headerAction}</div>
        ) : null}
      </div>
      {/* summary portion */}
      <div
        ref={contentRef}
        className="transition-all duration-500 ease-in-out overflow-hidden"
        style={{ maxHeight: "0px" }}
      >
        <h2 className="leading-5 font-semibold text-[0.763rem] text-gray-800 dark:text-gray-100">
          {" "}
          Profile Summary
        </h2>
        <p className="text-[0.763rem] text-gray-500 dark:text-gray-400">
          <InlineCitationText>{profile?.profile_summary}</InlineCitationText>
        </p>
      </div>

      {showFollowUpEmail && (
        <FollowUpEmail setShowFollowUpEmail={setShowFollowUpEmail} />
      )}
    </div>
  );
};

export default ProfileHeader;
